/**
 * @fileoverview Week view — 7-day time grid.
 *
 * Adapted from upstream: removed DnD/dialog wrappers. Clicking an empty
 * 15-minute slot fires `onDateCellClick` with the corresponding time.
 */

import {
  startOfWeek,
  addDays,
  format,
  parseISO,
  isSameDay,
  areIntervalsOverlapping,
} from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { ScrollArea } from "@/ui/primitives/scroll-area";

import { EventBlock } from "@/calendar/components/week-and-day-view/event-block";
import { CalendarTimeline } from "@/calendar/components/week-and-day-view/calendar-time-line";
import { WeekViewMultiDayEventsRow } from "@/calendar/components/week-and-day-view/week-view-multi-day-events-row";
import {
  useTimeGridSelection,
  formatMinLabel,
} from "@/calendar/components/week-and-day-view/use-time-grid-selection";

const HOUR_HEIGHT_PX = 96;

import { cn } from "@/utils/misc";
import {
  groupEvents,
  getEventBlockStyle,
  isWorkingHour,
  getVisibleHours,
} from "@/calendar/helpers";

import type { IEvent } from "@/calendar/interfaces";

interface IProps {
  singleDayEvents: IEvent[];
  multiDayEvents: IEvent[];
}

export function CalendarWeekView({ singleDayEvents, multiDayEvents }: IProps) {
  const { selectedDate, workingHours, visibleHours, onDateCellClick } =
    useCalendar();

  const { hours, earliestEventHour, latestEventHour } = getVisibleHours(
    visibleHours,
    singleDayEvents,
  );

  const weekStart = startOfWeek(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Drag-to-select a time range within a single day column (Notion-style). A
  // plain click resolves to a default one-hour slot. The `dayKey` is the
  // column index so the selection stays confined to the day it started in.
  const { selection, onColumnPointerDown, minToPx } = useTimeGridSelection({
    firstHour: earliestEventHour,
    totalHours: hours.length,
    hourHeightPx: HOUR_HEIGHT_PX,
    onSelect: (dayKey, startMin, endMin) => {
      const day = weekDays[Number(dayKey)];
      if (!day) return;
      const start = new Date(day);
      start.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
      const end = new Date(day);
      end.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
      onDateCellClick(start, end);
    },
  });

  return (
    <>
      <div className="flex flex-col items-center justify-center border-b py-4 text-sm text-muted-foreground sm:hidden">
        <p>Weekly view is not available on smaller devices.</p>
        <p>Please switch to daily or monthly view.</p>
      </div>

      <div className="hidden flex-col sm:flex">
        <div>
          <WeekViewMultiDayEventsRow
            selectedDate={selectedDate}
            multiDayEvents={multiDayEvents}
          />

          {/* Week header */}
          <div className="relative z-20 flex border-b">
            <div className="w-[72px]" />
            <div className="grid flex-1 grid-cols-7 divide-x border-l">
              {weekDays.map((day, index) => (
                <span
                  key={index}
                  className="py-2 text-center text-xs font-medium text-muted-foreground"
                >
                  {format(day, "EE")}{" "}
                  <span className="ml-1 font-semibold text-foreground">
                    {format(day, "d")}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>

        <ScrollArea className="h-[736px]" type="always">
          <div className="flex overflow-hidden">
            {/* Hours column */}
            <div className="relative w-[72px]">
              {hours.map((hour, index) => (
                <div key={hour} className="relative" style={{ height: "96px" }}>
                  <div className="absolute -top-3 right-2 flex h-6 items-center">
                    {index !== 0 && (
                      <span className="text-xs text-muted-foreground">
                        {format(new Date().setHours(hour, 0, 0, 0), "hh a")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Week grid */}
            <div className="relative flex-1 border-l">
              <div className="grid grid-cols-7 divide-x">
                {weekDays.map((day, dayIndex) => {
                  const dayEvents = singleDayEvents.filter(
                    (event) =>
                      isSameDay(parseISO(event.startDate), day) ||
                      isSameDay(parseISO(event.endDate), day),
                  );
                  const groupedEvents = groupEvents(dayEvents);

                  return (
                    <div
                      key={dayIndex}
                      className="relative"
                      style={{ height: hours.length * HOUR_HEIGHT_PX }}
                    >
                      {/* Interaction layer: click or drag to sweep a range.
                          Behind the event blocks so events stay clickable. */}
                      <div
                        className="absolute inset-0 cursor-pointer"
                        onMouseDown={(e) =>
                          onColumnPointerDown(e, String(dayIndex))
                        }
                        aria-label={`Drag to create an event on ${format(day, "EEE d MMM")}`}
                      />

                      {/* Hour guide lines + working-hour shading. */}
                      {hours.map((hour, index) => {
                        const isDisabled = !isWorkingHour(
                          day,
                          hour,
                          workingHours,
                        );

                        return (
                          <div
                            key={hour}
                            className={cn(
                              "pointer-events-none relative",
                              isDisabled && "bg-muted/40",
                            )}
                            style={{ height: "96px" }}
                          >
                            {index !== 0 && (
                              <div className="absolute inset-x-0 top-0 border-b" />
                            )}
                            <div className="absolute inset-x-0 top-1/2 border-b border-dashed" />
                          </div>
                        );
                      })}

                      {/* Live drag-selection highlight for this column. */}
                      {selection &&
                        selection.dayKey === String(dayIndex) &&
                        selection.endMin > selection.startMin && (
                          <div
                            className="pointer-events-none absolute inset-x-0 z-10 mx-0.5 rounded-md border border-primary/50 bg-primary/15"
                            style={{
                              top: minToPx(selection.startMin),
                              height:
                                minToPx(selection.endMin) -
                                minToPx(selection.startMin),
                            }}
                          >
                            <span className="m-0.5 block rounded bg-primary px-1 text-[9px] font-medium leading-tight text-primary-foreground">
                              {formatMinLabel(selection.startMin)}
                            </span>
                          </div>
                        )}

                      {groupedEvents.map((group, groupIndex) =>
                        group.map((event) => {
                          const baseStyle = getEventBlockStyle(
                            event,
                            day,
                            groupIndex,
                            groupedEvents.length,
                            {
                              from: earliestEventHour,
                              to: latestEventHour,
                            },
                          );
                          const hasOverlap = groupedEvents.some(
                            (otherGroup, otherIndex) =>
                              otherIndex !== groupIndex &&
                              otherGroup.some((otherEvent) =>
                                areIntervalsOverlapping(
                                  {
                                    start: parseISO(event.startDate),
                                    end: parseISO(event.endDate),
                                  },
                                  {
                                    start: parseISO(otherEvent.startDate),
                                    end: parseISO(otherEvent.endDate),
                                  },
                                ),
                              ),
                          );

                          const style = hasOverlap
                            ? baseStyle
                            : { ...baseStyle, width: "100%", left: "0%" };

                          return (
                            <div
                              key={event.id}
                              className="absolute p-1"
                              style={style}
                            >
                              <EventBlock event={event} />
                            </div>
                          );
                        }),
                      )}
                    </div>
                  );
                })}
              </div>

              <CalendarTimeline
                firstVisibleHour={earliestEventHour}
                lastVisibleHour={latestEventHour}
              />
            </div>
          </div>
        </ScrollArea>
      </div>
    </>
  );
}
